
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { LoaderIcon } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";

interface GoogleAuthButtonProps {
  onSuccess?: () => void;
  onError?: (message: string) => void;
}

export default function GoogleAuthButton({ onSuccess, onError }: GoogleAuthButtonProps) {
  const [loading, setLoading] = useState(false);
  const { t } = useLanguage();

  const handleGoogleSignIn = async () => {
    try {
      setLoading(true);
      
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}/`
        }
      });

      if (error) throw error;
      
      // If we reach here, the OAuth flow was initiated successfully
      // The actual redirect will happen automatically
      
    } catch (error: any) {
      const errorMessage = error.message || "Failed to sign in with Google";
      toast.error(errorMessage);
      
      if (onError) onError(errorMessage);
      setLoading(false);
    }
  };

  return (
    <Button 
      type="button"
      variant="outline" 
      className="w-full flex items-center gap-3 h-12" 
      onClick={handleGoogleSignIn}
      disabled={loading}
    >
      {loading ? (
        <LoaderIcon className="h-6 w-6 animate-spin" />
      ) : (
        <svg className="h-7 w-7" viewBox="0 0 48 48">
          <path 
            fill="#FFC107" 
            d="m43.611 20.083h-1.611v-.083h-18v8h11.303c-1.649 4.657-6.08 8-11.303 8-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657c-3.572-3.329-8.35-5.382-13.618-5.382-11.045 0-20 8.955-20 20s8.955 20 20 20 20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z"
          />
          <path 
            fill="#FF3D00" 
            d="m6.306 14.691 6.571 4.819c1.778-4.402 6.084-7.51 11.123-7.51 3.059 0 5.842 1.154 7.961 3.039l5.657-5.657c-3.572-3.329-8.35-5.382-13.618-5.382-7.682 0-14.344 4.337-17.694 10.691z"
          />
          <path 
            fill="#4CAF50" 
            d="m24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238c-2.008 1.521-4.504 2.43-7.219 2.43-5.202 0-9.619-3.317-11.283-7.946l-6.522 5.025c3.31 6.477 10.032 10.921 17.805 10.921z"
          />
          <path 
            fill="#1976D2" 
            d="m43.611 20.083h-1.611v-.083h-18v8h11.303c-.792 2.237-2.231 4.166-4.087 5.571.001-.001.002-.001.003-.002l6.19 5.238c-.438.398 6.591-4.807 6.591-14.807 0-1.341-.138-2.65-.389-3.917z"
          />
        </svg>
      )}
      <span className="text-lg font-medium">
        {loading ? t('auth.signingIn') : t('auth.signInWithGoogle')}
      </span>
    </Button>
  );
}
